import React, { useState, useRef, useEffect } from 'react';
import { apiService } from '../../services/api';
import '../shared.css';
import './NLQPanel.css';

interface NLQPanelProps {
  kgId: string;
  kgName: string;
  versionId: string;
  onQuerySuggested: (cypher: string) => void;
  onCollapsedChange?: (collapsed: boolean) => void;
}

const POLL_INTERVAL_MS = 1500;
const POLL_MAX_ATTEMPTS = 40;

function extractCypher(content: string): string {
  const fenced = content.match(/```(?:cypher)?\s*([\s\S]*?)```/i);
  if (fenced) return fenced[1].trim();
  const lines = content.split('\n');
  const cypherLine = lines.find(l =>
    /^\s*(MATCH|RETURN|CREATE|MERGE|CALL|SHOW|WITH|WHERE|UNWIND)/i.test(l)
  );
  if (cypherLine) return cypherLine.trim();
  return content.trim();
}

export const NLQPanel: React.FC<NLQPanelProps> = ({
  kgId,
  kgName,
  versionId,
  onQuerySuggested,
  onCollapsedChange,
}) => {
  const [input, setInput] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [collapsed, setCollapsed] = useState(true);
  const conversationIdRef = useRef<string | null>(null);
  const abortRef = useRef(false);
  const pendingTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    conversationIdRef.current = null;
  }, [kgId, versionId]);

  useEffect(() => {
    const timers = pendingTimersRef.current;
    return () => {
      abortRef.current = true;
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, []);

  const scheduleTimer = (fn: () => void, ms: number) => {
    const id = setTimeout(() => { pendingTimersRef.current.delete(id); fn(); }, ms);
    pendingTimersRef.current.add(id);
  };

  const pollUntilComplete = (convId: string, msgId: string): Promise<string> =>
    new Promise((resolve, reject) => {
      let attempts = 0;
      const tick = async () => {
        if (abortRef.current) return reject(new Error('aborted'));
        if (++attempts > POLL_MAX_ATTEMPTS) return reject(new Error('Timed out waiting for response'));
        try {
          const res: any = await apiService.coScientistGetMessage(convId, msgId);
          const st = res?.data?.attributes?.meta_data?.status;
          if (st === 'COMPLETED' || st === 'CANCELLED') {
            resolve(res?.data?.attributes?.content || '');
          } else {
            scheduleTimer(tick, POLL_INTERVAL_MS);
          }
        } catch (e) { reject(e); }
      };
      scheduleTimer(tick, POLL_INTERVAL_MS);
    });

  const pollForAgentResponse = (convId: string, userMsgId: string): Promise<string> =>
    new Promise((resolve, reject) => {
      let attempts = 0;
      const userIdNum = parseInt(userMsgId, 10);
      const tick = async () => {
        if (abortRef.current) return reject(new Error('aborted'));
        if (++attempts > POLL_MAX_ATTEMPTS) return reject(new Error('Timed out waiting for agent response'));
        try {
          const res: any = await apiService.coScientistListMessages(convId, 50);
          const messages: any[] = res?.data ?? [];
          const agentMsg = messages.find((m: any) => {
            const meta = m?.attributes?.meta_data;
            return (
              m?.attributes?.role === 'agent' &&
              meta?.source_message_id === userIdNum &&
              (meta?.status === 'COMPLETED' || meta?.status === 'CANCELLED')
            );
          });
          if (agentMsg) {
            resolve(agentMsg?.attributes?.content || '');
          } else {
            scheduleTimer(tick, POLL_INTERVAL_MS);
          }
        } catch (e) { reject(e); }
      };
      scheduleTimer(tick, POLL_INTERVAL_MS);
    });

  const ask = async (nlq: string) => {
    const query = nlq.trim();
    if (!query || status === 'loading') return;

    setStatus('loading');
    setErrorMsg('');
    abortRef.current = false;

    try {
      let conversationId = conversationIdRef.current;

      const systemPrompt = `You are helping a user write Cypher queries for a Neo4j knowledge graph.
The KG is named "${kgName}" (id: ${kgId}, version: ${versionId}).
When asked, respond ONLY with the Cypher query inside a \`\`\`cypher\`\`\` code block. No explanation needed.`;

      if (!conversationId) {
        const createRes: any = await apiService.coScientistCreateConversation({
          initialMessage: systemPrompt,
          kgId,
          kgName,
          versionId,
        });
        conversationId = createRes?.data?.id;
        if (!conversationId) throw new Error('Failed to create conversation');
        conversationIdRef.current = conversationId;

        const initMsgId = createRes?.included?.[0]?.id;
        if (initMsgId) await pollUntilComplete(conversationId, initMsgId);
      }

      const sendRes: any = await apiService.coScientistSendMessage(conversationId, query);
      const messageId = sendRes?.data?.id;
      if (!messageId) throw new Error('Failed to send message');

      const agentContent = await pollForAgentResponse(conversationId, messageId);
      onQuerySuggested(extractCypher(agentContent));
      setStatus('idle');
      setInput('');
    } catch (err: any) {
      if (abortRef.current) return;
      setStatus('error');
      setErrorMsg(
        err?.response?.data?.errors?.[0]?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to get suggestion'
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      ask(input);
    }
  };

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    onCollapsedChange?.(next);
  };

  return (
    <div className="nlq-panel">
      <div
        className={`nlq-header${collapsed ? '' : ' nlq-header--expanded'}`}
        onClick={toggleCollapsed}
      >
        <div className="nlq-header-left">
          <span className="nlq-chevron">{collapsed ? '▶' : '▼'}</span>
          <span className="nlq-title">Ask Co-Scientist</span>
          <span className="nlq-beta-badge">Beta</span>
        </div>
        <span className="nlq-subtitle">Natural language → Cypher</span>
      </div>

      {!collapsed && (
        <div className="nlq-body">
          <textarea
            className="nlq-textarea"
            placeholder={"e.g. How many genes are connected to diseases?\n\nCtrl+Enter / ⌘↵ to ask"}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={status === 'loading'}
            autoComplete="off"
            spellCheck={false}
          />

          {status === 'loading' && (
            <div className="nlq-loading-row">
              <span className="tab-spinner" style={{ width: 13, height: 13, borderWidth: 2 }} />
              <span className="nlq-loading-text">Asking Co-Scientist…</span>
            </div>
          )}

          {status === 'error' && (
            <div className="nlq-error">{errorMsg}</div>
          )}

          <button
            className={`nlq-submit-btn${status === 'loading' ? ' nlq-submit-btn--loading' : ''}`}
            onClick={() => ask(input)}
            disabled={status === 'loading' || !input.trim()}
          >
            {status === 'loading'
              ? <span className="inline-spinner" />
              : 'Ask Co-Scientist'
            }
          </button>
        </div>
      )}
    </div>
  );
};

export default NLQPanel;
